import * as z from "zod";
import { MultiplayerStateSchema } from "../types/schemas/general";
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from "react";
import { useWebsocket } from "../hooks/useWebsocket";
import { INITIAL_PLAYER_OBJECT } from "../utils/constants";

export type MultiplayerState = z.infer<typeof MultiplayerStateSchema>;
type WebsocketActions = Pick<
    ReturnType<typeof useWebsocket>,
    | "disconnect"
    | "hostGame"
    | "joinGame"
    | "sendMultiplayerMove"
    | "forfeitGame"
    | "requestToPlayAgain"
    | "responseToPlayAgainRequest"
    | "handleReturnMainMenu"
>;

export type MultiplayerContextType = {
    setMultiplayerState: Dispatch<SetStateAction<MultiplayerState>>;
    multiplayerState: MultiplayerState;
} & WebsocketActions;

export const MultiplayerContext = createContext<MultiplayerContextType | null>(null);

export const useMultiplayer = () => {
    const context = useContext(MultiplayerContext);
    if (!context) throw new Error("Multiplayer context is null");
    return context;
};

export const INITIAL_MULTIPLAYER_STATE: MultiplayerState = {
    isHost: false,
    lobbyId: "",
    waitingForPlayers: false,
    playerColor: "w",
    userId: "",
    p1: INITIAL_PLAYER_OBJECT,
    p2: INITIAL_PLAYER_OBJECT,
    forfeit: {
        isForfeit: false,
    },
    showUserNamePage: false,
    playAgainRequest: {
        sentRequest: false,
        receivedRequest: false,
    },
    playAgainResponse: {
        sentResponse: false,
        receivedResponse: false,
    },
};

export const MultiplayerProvider = ({ children }: { children: ReactNode }): JSX.Element => {
    const [multiplayerState, setMultiplayerState] =
        useState<MultiplayerState>(INITIAL_MULTIPLAYER_STATE);

    const {
        hostGame,
        joinGame,
        disconnect,
        sendMultiplayerMove,
        forfeitGame,
        requestToPlayAgain,
        responseToPlayAgainRequest,
        handleReturnMainMenu,
    } = useWebsocket(setMultiplayerState, multiplayerState);

    const contextValue = {
        setMultiplayerState,
        multiplayerState,
        hostGame,
        joinGame,
        disconnect,
        sendMultiplayerMove,
        forfeitGame,
        requestToPlayAgain,
        responseToPlayAgainRequest,
        handleReturnMainMenu,
    } satisfies MultiplayerContextType;

    return (
        <MultiplayerContext.Provider value={contextValue}>{children}</MultiplayerContext.Provider>
    );
};

export default MultiplayerProvider;
